import { useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../components/Card';
import Select from '../components/Select';
import Button from '../components/Button';
import apiClient from '../lib/api';
import { useThemeStore } from '../store/themeStore';

interface UserSettings {
  themePreference: 'system' | 'light' | 'dark';
  preferredLanguage: 'en' | 'nl';
}

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useThemeStore();
  const [settings, setSettings] = useState<UserSettings>({
    themePreference: theme,
    preferredLanguage: (i18n.language as 'en' | 'nl') || 'en',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Fetch current settings on mount
  useEffect(() => {
    async function fetchSettings() {
      try {
        const response = await apiClient.get('/auth/settings');
        const data = response.data;
        setSettings({
          themePreference: data.themePreference || 'system',
          preferredLanguage: data.preferredLanguage || 'en',
        });
        setTheme(data.themePreference || 'system');
        i18n.changeLanguage(data.preferredLanguage || 'en');
      } catch (err) {
        console.error('Failed to fetch settings:', err);
        setError('Failed to load settings');
      } finally {
        setIsLoading(false);
      }
    }

    fetchSettings();
  }, [setTheme]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSaving(true);

    try {
      const response = await apiClient.patch('/auth/settings', {
        themePreference: settings.themePreference,
        preferredLanguage: settings.preferredLanguage,
      });

      setSuccessMessage(response.data.message || t('settings.appearance.saveSuccess'));

      // Update theme in store (triggers localStorage + DOM update)
      setTheme(settings.themePreference);

      // Update language in i18n (triggers language change)
      i18n.changeLanguage(settings.preferredLanguage);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Failed to save settings:', err);
      setError(err.response?.data?.message || t('settings.appearance.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <h1 className="text-3xl font-bold mb-6 dark:text-white">{t('settings.title')}</h1>
        <Card>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Account Settings Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2 dark:text-white">
          {t('settings.account.title', 'Account Settings')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('settings.account.description', 'Manage your account preferences and appearance')}
        </p>
      </div>

      {/* Appearance Settings */}
      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <h2 className="text-xl font-bold mb-4 dark:text-white">
              {t('settings.appearance.title')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('settings.appearance.description')}
            </p>
          </div>

          {/* Language Preference */}
          <Select
            label={t('settings.appearance.language')}
            value={settings.preferredLanguage}
            onChange={(e) => {
              const newLanguage = e.target.value as 'en' | 'nl';
              setSettings({ ...settings, preferredLanguage: newLanguage });
              // Apply language immediately (before save)
              i18n.changeLanguage(newLanguage);
            }}
          >
            <option value="en">{t('settings.appearance.languageOptions.en')}</option>
            <option value="nl">{t('settings.appearance.languageOptions.nl')}</option>
          </Select>

          {/* Theme Preference */}
          <Select
            label={t('settings.appearance.theme')}
            value={settings.themePreference}
            onChange={(e) => {
              const newTheme = e.target.value as 'system' | 'light' | 'dark';
              setSettings({ ...settings, themePreference: newTheme });
              // Apply theme immediately (before save)
              setTheme(newTheme);
            }}
          >
            <option value="system">{t('settings.appearance.themeOptions.system')}</option>
            <option value="light">{t('settings.appearance.themeOptions.light')}</option>
            <option value="dark">{t('settings.appearance.themeOptions.dark')}</option>
          </Select>

          {/* Error Message */}
          {error && (
            <div className="p-3 border-2 border-red-500 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200 text-sm">
              {error}
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="p-3 border-2 border-green-500 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-200 text-sm">
              {successMessage}
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? t('settings.appearance.saving') : t('settings.appearance.save')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
